import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import Icon from '@msi/cobalt-react/icon';
import Button from '@msi/cobalt-react/button';
import { addToast } from '@msi/cobalt-react/toast';
import { Dropdown, DropdownItem } from '@msi/cobalt-react/dropdown';
import ApiService from '../../services/apiService';
import SidebarApi from '../../components/ApiUriMenu/SidebarApi';
import DetailService from '../../services/detailService';
import ApiDetailsComponent from '../../components/ApiDetails/ApiDetails';
import ApiEndpoints from '../../components/ApiEndpoints/apiEndpoints';
import GetUpdatesModal from '../../components/GetUpdatesModal/GetUpdatesModal';
import CompareVersionsModal from '../../components/CompareVersions/CompareVersionsModal';
import SubscriptionService from '../../services/subscriptionService';
import { Api } from '../../types/apiData';
import './api_details.css';

function mapAreasToFields(areas: any[] = []) {
    const areaMap: Record<number, string> = {
        1: 'linesOfBusiness',
        2: 'productNameTechnology',
        3: 'area',
        4: 'subArea1',
        5: 'subArea2',
    };
    const result: Record<string, string> = {};
    areas.forEach((a: any) => {
        const key = areaMap[a.level];
        if (key) result[key] = a.name;
    });
    return result;
}

interface SubscriptionFeedbackState {
    loading: boolean;
    error: string | null;
    success: boolean;
}

const ApiDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { authState } = useOktaAuth();
    const navigate = useNavigate();
    const [api, setApi] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [isHidden, setIsHidden] = useState(true);
    const [apiSpecificationContent, setApiSpecificationContent] = useState<any>(null);
    const [apiPaths, setApiPaths] = useState<any[]>([]);
    const [apiComponents, setApiComponents] = useState<any[]>([]);
    const [selectedEndpoint, setSelectedEndpoint] = useState<any>(null);
    const [isGetUpdatesModalOpen, setIsGetUpdatesModalOpen] = useState(false);
    const [subscriptionEmail, setSubscriptionEmail] = useState('');
    const [subscriptionFeedback, setSubscriptionFeedback] = useState<SubscriptionFeedbackState>({
        loading: false,
        error: null,
        success: false,
    });
    const [subscriptionsByApiId, setSubscriptionsByApiId] = useState<Record<string, string>>({});
    const [updatesActionLoading, setUpdatesActionLoading] = useState(false);
    const [apiVersions, setApiVersions] = useState<Api[]>([]);
    const [selectedVersionId, setSelectedVersionId] = useState<string>('');
    const [showVersionDropdown, setShowVersionDropdown] = useState(false);
    const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);

    const apiService = new ApiService();
    const detailService = new DetailService();
    const subscriptionService = new SubscriptionService();

    // Reset endpoint state when navigating to a different API/version
    useEffect(() => {
        setApiSpecificationContent(null);
        setApiPaths([]);
        setApiComponents([]);
        setSelectedEndpoint(null);
    }, [id]);

    useEffect(() => {
        const fetchApi = async () => {
            setLoading(true);
            try {
                if (id === undefined) {
                    throw new Error('API ID is required');
                }
                const result = await apiService.getApiById(id, authState);
                
                // Fetch all versions for this API
                const parentId = result.parentApiId || id;
                try {
                    const versions = await apiService.getApiVersions(parentId, authState);
                    
                    // Remove duplicate versions: if parent has same version number as a child version, keep only the child
                    const uniqueVersions: any[] = [];
                    const versionNumbers = new Set<string>();
                    
                    // Add all child versions (those with parentApiId)
                    versions.forEach(v => {
                        if (v.parentApiId) {
                            uniqueVersions.push(v);
                            versionNumbers.add(v.latestApiVersionNumber);
                        }
                    });
                    
                    // Add parent only if its version number isn't already in the list
                    versions.forEach(v => {
                        if (!v.parentApiId && !versionNumbers.has(v.latestApiVersionNumber)) {
                            uniqueVersions.push(v);
                        }
                    });
                    
                    // Backend already sorts by apiLaunchDate DESC, so use that order directly
                    // Versions are already ordered by the database query
                    setApiVersions(uniqueVersions);
                    
                    // If loaded the parent API and there are child versions, switch to the latest version
                    if (!result.parentApiId && uniqueVersions.length > 0) {
                        const latestVersion = uniqueVersions.find(v => v.isLatestVersion) || uniqueVersions[0];
                        if (latestVersion && latestVersion.id !== id) {
                            console.log(`Switching from parent to latest version: ${latestVersion.id}`);
                            const latestVersionData = await apiService.getApiById(latestVersion.id, authState);
                            const areaFields = mapAreasToFields(latestVersionData.areas);
                            setApi({ ...latestVersionData, ...areaFields });
                            setSelectedVersionId(latestVersion.id);
                            return;
                        }
                    }
                } catch (versionError) {
                    console.error('Error fetching API versions:', versionError);
                    setApiVersions([result]);
                }
                
                // Map areas to fields and set the API data
                const areaFields = mapAreasToFields(result.areas);
                setApi({ ...result, ...areaFields });
                setSelectedVersionId(id);
            } catch (error) {
                setApi(null);
            } finally {
                setLoading(false);
            }
        };
        if (id && authState) fetchApi();
    }, [id, authState]);

    useEffect(() => {
        const fetchSubscriptions = async () => {
            if (!authState?.isAuthenticated) {
                setSubscriptionsByApiId({});
                return;
            }

            try {
                const userSubscriptions = await subscriptionService.getMySubscriptions(authState);
                const nextSubscriptions = userSubscriptions.reduce<Record<string, string>>((acc, subscription) => {
                    if (subscription.apiId && subscription.id) {
                        acc[subscription.apiId] = subscription.id;
                    }
                    return acc;
                }, {});
                setSubscriptionsByApiId(nextSubscriptions);
            } catch (error) {
                console.error('Error fetching user subscriptions:', error);
            }
        };

        fetchSubscriptions();
    }, [authState]);

    const fetchApiSpecificationContent = async (urlObject: string, apiId: string) => {
        console.log('Fetching API specification content from:', urlObject);
        if (!authState) {
            console.error("Auth state is null or undefined.");
            return;
        }
        setLoading(true);
        try {
            const response = await detailService.fetchFileContent(urlObject, apiId, authState);
            setApiSpecificationContent(response);
        } catch (error) {
            console.error("Error fetching API specification content:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (api?.id && api?.apiSpecification) {
            // Use api.id instead of URL param id — when a parent auto-redirects
            // to the latest version, id (URL) stays as the parent but api is the child version.
            // The spec file is stored under the child version's ID in GCS.
            const specUrl = typeof api.apiSpecification === 'string'
                ? api.apiSpecification
                : api.apiSpecification.url;

            if (specUrl) {
                fetchApiSpecificationContent(specUrl, api.id);
            } else {
                // No URL available — clear endpoints
                setApiSpecificationContent(null);
                setApiPaths([]);
                setApiComponents([]);
            }
        } else {
            // No spec at all — clear endpoints from previous version
            setApiSpecificationContent(null);
            setApiPaths([]);
            setApiComponents([]);
        }
    }, [api?.apiSpecification, api?.id]);

    const handleEndpoints = async (apiSpecificationContent: any) => {
        try {
            // Now apiSpecificationContent is the FileContentResponse object
            const endpoints = await detailService.getApiSpecData(apiSpecificationContent);

                // Flatten the paths structure: each path can have multiple HTTP methods
                const apiUri: any[] = [];

                // Create a map of schema names to their details for quick lookup
                const schemaMap = new Map();
                Object.entries(endpoints.components.schemas || {}).forEach(([schemaName, schemaDetails]) => {
                    schemaMap.set(schemaName, schemaDetails);
                });

                // Helper function to enrich content with schema details
                const enrichContentWithSchemas = (contentObj: any) => {
                    if (contentObj) {
                        Object.entries(contentObj).forEach(([mediaType, content]: [string, any]) => {
                            if (content.schema) {

                                let refSchemaName = null;
                                let schemaDetails = null;

                                // Check for direct $ref
                                if (content.schema.$ref) {
                                    refSchemaName = content.schema.$ref.split('/').pop();
                                    schemaDetails = schemaMap.get(refSchemaName);

                                    if (schemaDetails) {
                                        content.schema = {
                                            ...content.schema,
                                            resolvedSchema: {
                                                name: refSchemaName,
                                                ...schemaDetails
                                            }
                                        };
                                    }
                                }
                                // Check for array items $ref (like your case)
                                else if (content.schema.type === 'array' && content.schema.items?.$ref) {
                                    refSchemaName = content.schema.items.$ref.split('/').pop();
                                    schemaDetails = schemaMap.get(refSchemaName);

                                    if (schemaDetails) {
                                        content.schema = {
                                            ...content.schema,
                                            resolvedSchema: {
                                                name: refSchemaName,
                                                type: 'array',
                                                items: {
                                                    name: refSchemaName,
                                                    ...schemaDetails
                                                },
                                                // For UI compatibility, also add properties at root level
                                                properties: schemaDetails.properties
                                            }
                                        };
                                    }
                                }
                                // Handle inline properties
                                else if (content.schema.properties) {
                                    Object.entries(content.schema.properties).forEach(([propName, propDetails]: [string, any]) => {
                                        if (propDetails.$ref) {
                                            const propRefSchemaName = propDetails.$ref.split('/').pop();
                                            const propSchemaDetails = schemaMap.get(propRefSchemaName);
                                            if (propSchemaDetails) {
                                                content.schema.properties[propName] = {
                                                    ...propDetails,
                                                    resolvedSchema: {
                                                        name: propRefSchemaName,
                                                        ...propSchemaDetails
                                                    }
                                                };
                                            }
                                        }
                                    });

                                    // Create a resolved schema for inline properties
                                    content.schema.resolvedSchema = {
                                        name: 'inline',
                                        type: content.schema.type,
                                        properties: content.schema.properties
                                    };
                                    console.log(`  ✅ Successfully processed inline properties`);
                                }

                                if (!refSchemaName && !content.schema.properties) {
                                    console.log(`  ⚠️ No $ref or properties found in schema:`, content.schema);
                                }
                            } else {
                                console.log(`  ❌ No schema found for ${mediaType}`);
                            }
                        });
                    } else {
                        console.log('Content object is null or undefined');
                    }
                    return contentObj;
                };

                Object.entries(endpoints.paths || {}).forEach(([pathKey, pathValue]: [string, any]) => {
                    // Each pathValue contains HTTP methods (get, post, put, delete, etc.)
                    Object.entries(pathValue).forEach(([method, methodDetails]: [string, any]) => {
                        // Process responses to enrich them with schema details
                        const enrichedResponses = { ...methodDetails.responses };
                        const enrichedRequestBody = methodDetails.requestBody ? { ...methodDetails.requestBody } : null;

                        // Enrich responses
                        if (enrichedResponses) {
                            Object.entries(enrichedResponses).forEach(([statusCode, responseData]: [string, any]) => {
                                if (responseData.content) {
                                    enrichContentWithSchemas(responseData.content);
                                }
                            });
                        }

                        // Enrich request body
                        if (enrichedRequestBody && enrichedRequestBody.content) {
                            enrichContentWithSchemas(enrichedRequestBody.content);
                        }

                        apiUri.push({
                            id: `${method.toUpperCase()}_${pathKey}`, // Unique identifier
                            path: pathKey, // The actual path like '/v1/device'
                            method: method.toUpperCase(), // HTTP method like 'PUT', 'GET'  
                            ...methodDetails, // Spread all method details (description, summary, parameters, responses, etc.)
                            responses: enrichedResponses, // Use the enriched responses
                            requestBody: enrichedRequestBody // Use the enriched request body
                        });
                    });
                });

                const components: any[] = [];

                Object.entries(endpoints.components.schemas || {}).forEach(([componentName, componentDetails]: [string, any]) => {
                    components.push({
                        name: componentName,
                        ...componentDetails
                    });
                });

                setApiPaths(apiUri);
                setApiComponents(components);
                //console.log('Parsed API URI with enriched responses:', apiUri);
                //console.log('API components:', components);
        } catch (error) {
            console.error('Error processing API specification content:', error);
        }
    }

    useEffect(() => {
        if (apiSpecificationContent) {
            handleEndpoints(apiSpecificationContent);
        }
    }, [apiSpecificationContent]);

    if (loading) return <div className="container mt-4">Loading...</div>;
    if (!api) return <div className="container mt-4">API not found.</div>;

    const toggleIcon = () => {
        console.log('content is hidden', isHidden);
        setIsHidden(!isHidden);
        console.log('content is show', !isHidden);
    }

    const getDefaultUserEmail = () => {
        const claims = authState?.idToken?.claims as { email?: string } | undefined;
        return claims?.email ?? '';
    };

    const subscriptionApiId = api?.parentApiId || api?.id || '';
    const currentSubscriptionId = subscriptionApiId ? subscriptionsByApiId[subscriptionApiId] : undefined;
    const isSubscribedToApi = Boolean(currentSubscriptionId);

    const openGetUpdatesModal = () => {
        setSubscriptionEmail(getDefaultUserEmail());
        setSubscriptionFeedback({ loading: false, error: null, success: false });
        setIsGetUpdatesModalOpen(true);
    };

    const closeGetUpdatesModal = () => {
        if (subscriptionFeedback.loading) {
            return;
        }
        setIsGetUpdatesModalOpen(false);
    };

    const handleSubscribeToUpdates = async () => {
        const targetApiId = api?.parentApiId || api?.id;

        if (!targetApiId || !authState) {
            return;
        }

        const normalizedEmail = subscriptionEmail.trim();

        if (!normalizedEmail) {
            addToast('error', 'Email is required to subscribe.', {
                containerId: 'default',
            });
            return;
        }

        try {
            setSubscriptionFeedback({ loading: true, error: null, success: false });
            const response = await subscriptionService.subscribeToApi(targetApiId, normalizedEmail, authState);
            if (response.id) {
                setSubscriptionsByApiId((prev) => ({
                    ...prev,
                    [targetApiId]: response.id as string,
                }));
            } else {
                const userSubscriptions = await subscriptionService.getMySubscriptions(authState);
                const nextSubscriptions = userSubscriptions.reduce<Record<string, string>>((acc, subscription) => {
                    if (subscription.apiId && subscription.id) {
                        acc[subscription.apiId] = subscription.id;
                    }
                    return acc;
                }, {});
                setSubscriptionsByApiId(nextSubscriptions);
            }
            setSubscriptionFeedback({
                loading: false,
                error: null,
                success: true,
            });
            addToast('success', `You are subscribed to updates for ${api.fullApiName || 'this API'}.`, {
                containerId: 'default',
            });
            closeGetUpdatesModal();
        } catch (error: any) {
            const fallbackMessage = error?.response?.data?.message || 'Unable to subscribe right now. Please try again later.';
            setSubscriptionFeedback({ loading: false, error: fallbackMessage, success: false });
            addToast('error', fallbackMessage, {
                containerId: 'default',
            });
        }
    };

    const handleUnsubscribeFromUpdates = async () => {
        if (!currentSubscriptionId || !subscriptionApiId || !authState) {
            return;
        }

        try {
            setUpdatesActionLoading(true);
            await subscriptionService.unsubscribe(currentSubscriptionId, authState);
            setSubscriptionsByApiId((prev) => {
                const updated = { ...prev };
                delete updated[subscriptionApiId];
                return updated;
            });
            addToast('success', `You have unsubscribed from updates for ${api?.fullApiName || 'this API'}.`, {
                containerId: 'default',
            });
        } catch (error: any) {
            const fallbackMessage = error?.response?.data?.message || 'Unable to unsubscribe right now. Please try again later.';
            addToast('error', fallbackMessage, {
                containerId: 'default',
            });
        } finally {
            setUpdatesActionLoading(false);
        }
    };

    const handleUpdatesAction = () => {
        if (isSubscribedToApi) {
            handleUnsubscribeFromUpdates();
            return;
        }
        openGetUpdatesModal();
    };

    const clearSubscriptionEmail = () => setSubscriptionEmail('');

    const handleVersionChange = (versionId: string) => {
        setSelectedVersionId(versionId);
        navigate(`/api-details/${versionId}`);
    };

    const handleCompareVersions = (fromVersionNumber: string, toVersionNumber: string) => {
        const parentId = api?.parentApiId || api?.id;
        setIsCompareModalOpen(false);
        navigate(`/compare/${parentId}?from=${encodeURIComponent(fromVersionNumber)}&to=${encodeURIComponent(toVersionNumber)}`);
    };

    const updatesHelperText = `Subscribe to the ${api?.fullApiName || 'API'} change log to be up to date on recent changes.`;

    return (
        <div className="container">
            <GetUpdatesModal
                isOpen={isGetUpdatesModalOpen}
                email={subscriptionEmail}
                helperText={updatesHelperText}
                isSubmitting={subscriptionFeedback.loading}
                onEmailChange={setSubscriptionEmail}
                onClearEmail={clearSubscriptionEmail}
                onSubscribe={handleSubscribeToUpdates}
                onCancel={closeGetUpdatesModal}
            />
            <CompareVersionsModal
                isOpen={isCompareModalOpen}
                versions={apiVersions}
                defaultToId={selectedVersionId}
                onCompare={handleCompareVersions}
                onCancel={() => setIsCompareModalOpen(false)}
            />
            {/* Header Section */}
            <div className="NameAndVersion">
                <div className="d-flex flex-row align-items-center justify-content-between">
                    <div className="d-flex flex-row align-items-center">
                        <div onClick={() => navigate(-1)} className="menu-link" style={{ cursor: 'pointer' }}>
                            <Icon name="ic_back" size={36} color="primary" className="me-2" />
                        </div>
                        <div className='flex-column'>
                            <h4 className="mb-0">{api.fullApiName}</h4>
                            <div className="api-version">
                                <Dropdown
                                    anchor={() => (
                                        <Button variant="text" className="version-dropdown-trigger">
                                            <span>Version {api.latestApiVersionNumber}</span>
                                            { <Icon name="ic_arrow_down" size={16} />}
                                        </Button>
                                    )}
                                    className="version-selector-dropdown"
                                    position="bottom-start"
                                    isOpen={showVersionDropdown}
                                    onToggleOpen={setShowVersionDropdown}
                                    closeOnClick
                                    disabled={apiVersions.length <= 1 && !api.userCanEditAndDelete}
                                >
                                    {api.userCanEditAndDelete && (
                                        <DropdownItem
                                              className="add-new-version d-flex align-items-center justify-content-between"
                                              onClick={() => navigate('/add-record', { state: { api, shouldAddVersion: true } })}
                                        >
                                              Add New Version
                                              <Icon name="ic_add" size={20} className="ms-2" />
                                        </DropdownItem>
                                    )}
                                        <hr className="version-dropdown-divider" />
                                        <span className="dropdown-item-text fw-bold">Previous Versions</span>
                                    
                                    {apiVersions.map((version) => (
                                        <DropdownItem
                                            key={version.id}
                                            className={version.id === selectedVersionId ? 'selected' : ''}
                                            onClick={() => handleVersionChange(version.id)}
                                        >
                                            Version {version.latestApiVersionNumber}
                                            {version.isLatestVersion && ' (Latest)'}
                                        </DropdownItem>
                                    ))}
                                </Dropdown>
                            </div>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            text="Compare Versions"
                            disabled={apiVersions.length < 2}
                            onClick={() => setIsCompareModalOpen(true)}
                        />
                        <Button
                            text={isSubscribedToApi ? 'Unsubscribe' : 'Get Updates'}
                            buttonType="action"
                            disabled={updatesActionLoading || subscriptionFeedback.loading}
                            onClick={handleUpdatesAction}
                        />
                        {api.userCanEditAndDelete && (
                            <Button
                                text="Add New Version"
                                buttonType="action"
                                onClick={() => navigate('/add-record', { state: { api, shouldAddVersion: true } })}
                            >
                            </Button>
                        )}
                        {api.userCanEditAndDelete && (
                            <Button
                                onClick={(e: { stopPropagation: () => void; }) => {
                                    e.stopPropagation();
                                    navigate(`/add-record`, { state: { api: api } });
                                }}
                                >
                                Edit
                            </Button>
                        )}
                    </div>
                </div>
            </div>
            <div className="api-content">
                <div id="api-details">
                    <SidebarApi apiContent={apiPaths} onEndpointSelect={setSelectedEndpoint} hasGovernanceMetadata={!!api.apiCenterMetadata} />
                </div>
                <div id="cards-details">
                    <div id='intro-details'>
                        <ApiDetailsComponent api={api} />
                    </div>
                    <div className='endpoint-details'>
                        <ApiEndpoints selectedEndpoint={selectedEndpoint} />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ApiDetails;